// Phase 3D.3-B - Pure, deterministic marketing RECOMMENDATION engine for the
// Savoie pilot. It transforms the CANONICAL output of
// selectGrowthOpportunity(dataset) (see savoieOpportunity.js) into a single,
// explainable recommendation strictly bounded to the available evidence.
//
// SCOPE / SAFETY CONTRACT:
//   * PURE, SYNCHRONOUS, DETERMINISTIC. No side effects.
//   * NO network / NO HTTP / NO DNS, NO DB, NO AI/provider.
//   * NO Date.now(), NO randomness, NO environment read, NO storage.
//   * NO import with side effects (this module imports nothing).
//   * Does NOT render, does NOT create a page/route/component/backend.
//   * Does NOT create a campaign, budget, channel, audience size, calendar,
//     projection, ROI, expected result or inferred cause.
//   * Reads EXCLUSIVELY from its input; never recomputes/rounds evidence for
//     the decision; never hardcodes a market, name, period or figure.
//   * Emits at most ONE recommendation, for the single market of the input.
//   * Fail-closed: any contract violation yields an explicit { status:"none" }.

// Closed enum for the only supported recommendation action in this phase.
export const ACTION_TYPE = "audience_focus";

// Exact recommendation disclaimer (verbatim, required).
export const RECOMMENDATION_DISCLAIMER =
  "Recommandation déterministe dérivée d'une opportunité analytique issue de " +
  "données synthétiques de démonstration. Elle ne constitue ni une observation " +
  "réelle, ni un conseil client, ni une campagne.";

// Verbatim note attached to the context-only block.
export const CONTEXT_ONLY_NOTE =
  "Contexte uniquement — non utilisé pour produire la recommandation.";

// Deterministic reason tokens for the explicit "none" result.
export const RECO_NONE_REASONS = Object.freeze({
  INVALID_INPUT: "invalid_input",
  NO_OPPORTUNITY: "no_opportunity",
  INVALID_STATUS: "invalid_status",
  INVALID_SIMULATION: "invalid_simulation",
  INVALID_META: "invalid_meta",
  INVALID_OPPORTUNITY_ID: "invalid_opportunity_id",
  TYPE_MISMATCH: "type_mismatch",
  INVALID_MARKET: "invalid_market",
  INVALID_PERIOD: "invalid_period",
  INVALID_EVIDENCE: "invalid_evidence",
  INVALID_THRESHOLDS: "invalid_thresholds",
  THRESHOLD_NOT_MET: "threshold_not_met",
});

// --- Presentation-only formatter (fr-FR). Never used for the decision. ----
const _fmtPctSigned = (n) =>
  (n >= 0 ? "+" : "") + (Math.round(n * 10) / 10).toFixed(1).replace(".", ",");

// --- Pure validators ------------------------------------------------------
const _isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const _isNonEmptyStr = (v) => typeof v === "string" && v.length > 0;
const _isFiniteNum = (v) => typeof v === "number" && Number.isFinite(v);

// "YYYY-MM" with month 01..12 -> { year, month, mm } ; otherwise null.
function _parsePeriod(str) {
  if (typeof str !== "string") return null;
  const m = /^(\d{4})-(\d{2})$/.exec(str);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!Number.isInteger(year) || month < 1 || month > 12) return null;
  return { year, month, mm: m[2] };
}

// Add exactly one calendar year, keeping the same month (analytical follow-up
// period only; NOT a campaign date, NOT a projection). "2024-12" -> "2025-12".
function _addOneYear(str) {
  const p = _parsePeriod(str);
  if (!p) return null;
  return `${p.year + 1}-${p.mm}`;
}

// Meta must mirror the frozen synthetic-demo contract (no transformation).
function _metaValid(meta) {
  if (!_isObj(meta)) return false;
  if (meta.is_demo !== true) return false;
  if (meta.provenance !== "synthetic_demo") return false;
  if (meta.provenance_label !== "DONNÉES DE DÉMONSTRATION") return false;
  if (meta.affiliation !== "none") return false;
  if (meta.simulation !== false) return false;
  return true;
}

function _none(reason, meta) {
  return {
    status: "none",
    recommendation: null,
    reason,
    meta: _isObj(meta) ? meta : null,
    simulation: false,
  };
}

/**
 * Build at most ONE deterministic marketing recommendation from the canonical
 * output of selectGrowthOpportunity(dataset).
 * @param {object} opportunityResult - canonical opportunity result.
 * @returns recommendation object OR explicit { status:"none", ... }.
 */
export function buildGrowthRecommendation(opportunityResult) {
  const R = RECO_NONE_REASONS;

  // 1. Basic shape.
  if (!_isObj(opportunityResult)) return _none(R.INVALID_INPUT, null);

  const o = opportunityResult;
  const meta = o.meta;

  // 2. Explicit "none" input is propagated without fabricating anything.
  if (o.status === "none") return _none(R.NO_OPPORTUNITY, meta);

  // 3. Only a detected opportunity can produce a recommendation.
  if (o.status !== "detected") return _none(R.INVALID_STATUS, meta);

  // 4. Simulation must be strictly false.
  if (o.simulation !== false) return _none(R.INVALID_SIMULATION, meta);

  // 5. Demo meta / provenance contract (fail-closed, no transformation).
  if (!_metaValid(meta)) return _none(R.INVALID_META, meta);
  if (o.provenance !== "synthetic_demo") return _none(R.INVALID_META, meta);

  // 6. Opportunity identifier must be a usable, non-empty string.
  if (!_isNonEmptyStr(o.id)) return _none(R.INVALID_OPPORTUNITY_ID, meta);

  // 7. Growth-type consistency.
  if (o.type !== "growth") return _none(R.TYPE_MISMATCH, meta);

  // 8. Market identity (never hardcoded; taken verbatim from the input).
  if (!_isNonEmptyStr(o.market_code) || !_isNonEmptyStr(o.market_name)) {
    return _none(R.INVALID_MARKET, meta);
  }

  // 9. Periods must be strictly YYYY-MM with a real month.
  const evP = _parsePeriod(o.period);
  const cmpP = _parsePeriod(o.comparison_period);
  if (!evP || !cmpP) return _none(R.INVALID_PERIOD, meta);
  const monitoring = _addOneYear(o.period);
  if (!monitoring) return _none(R.INVALID_PERIOD, meta);

  // 10. Evidence structure + mandatory finite numeric fields.
  const ev = o.evidence;
  if (!_isObj(ev)) return _none(R.INVALID_EVIDENCE, meta);
  const {
    visitor_arrivals,
    previous_year_visitor_arrivals,
    growth_percent,
    market_share_percent,
  } = ev;
  if (
    !_isFiniteNum(visitor_arrivals) ||
    !_isFiniteNum(previous_year_visitor_arrivals) ||
    !_isFiniteNum(growth_percent) ||
    !_isFiniteNum(market_share_percent)
  ) {
    return _none(R.INVALID_EVIDENCE, meta);
  }

  // 11. Thresholds transmitted by the opportunity must be valid numbers.
  const th = o.thresholds;
  if (!_isObj(th)) return _none(R.INVALID_THRESHOLDS, meta);
  if (
    !_isFiniteNum(th.min_growth_percent) ||
    !_isFiniteNum(th.min_visitor_arrivals)
  ) {
    return _none(R.INVALID_THRESHOLDS, meta);
  }

  // 12. The transmitted thresholds must STILL be satisfied by the evidence.
  if (
    growth_percent < th.min_growth_percent ||
    visitor_arrivals < th.min_visitor_arrivals
  ) {
    return _none(R.THRESHOLD_NOT_MET, meta);
  }

  // --- Build the single recommendation (all values from the input) --------
  const growthStr = _fmtPctSigned(growth_percent);

  const objective =
    `Renforcer l'attention marketing portée au marché ${o.market_name}, ` +
    `dont les arrivées visiteurs progressent de ${growthStr} % sur ${o.period} ` +
    `par rapport à ${o.comparison_period}.`;

  const userFacingText =
    `Marché ${o.market_name} — recommandation : renforcer l'attention marketing ` +
    `sur la base d'une croissance de ${growthStr} % des arrivées visiteurs ` +
    `(${o.period} vs ${o.comparison_period}). ` +
    `Période de suivi analytique : ${monitoring}.`;

  const ctx = _isObj(o.context) ? o.context : null;

  return {
    status: "recommended",
    id: `reco:${o.id}`,
    based_on_opportunity_id: o.id,
    action_type: ACTION_TYPE,
    objective,
    audience: {
      source_market_code: o.market_code,
      source_market_name: o.market_name,
    },
    evidence_period: o.period,
    comparison_period: o.comparison_period,
    proposed_monitoring_period: monitoring,
    evidence_used: {
      visitor_arrivals,
      previous_year_visitor_arrivals,
      growth_percent,
      market_share_percent,
    },
    context_only: {
      average_stay: ctx ? ctx.average_stay ?? null : null,
      tourism_spend_per_visitor: ctx ? ctx.tourism_spend_per_visitor ?? null : null,
      currency: ctx ? ctx.currency ?? null : null,
      note: CONTEXT_ONLY_NOTE,
    },
    thresholds: th,
    ranking_method: o.ranking_method,
    provenance: "synthetic_demo",
    meta,
    simulation: false,
    campaign: null,
    disclaimer: RECOMMENDATION_DISCLAIMER,
    user_facing_text: userFacingText,
  };
}

export const _recommendationInternals = {
  _parsePeriod,
  _addOneYear,
  _metaValid,
  _fmtPctSigned,
};
